/** Small formatting helpers shared by the overlay, the ticker and the API. */

/** `mm:ss`, or `h:mm:ss` once past an hour. Negative values clamp to zero. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** `01:23:45` wall clock in the viewer's locale-free 24h form. */
export function formatTimeOfDay(date: Date): string {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

export function formatSeasonLabel(seasonNumber: number): string {
  return `SEASON ${String(seasonNumber).padStart(3, '0')}`;
}

export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** #RRGGBB -> 0xRRGGBB, for handing flag colours straight to PixiJS. */
export function hexToNumber(hex: string): number {
  return parseInt(hex.replace('#', ''), 16) >>> 0;
}

/** Perceived luminance (0..1) so text can pick black or white automatically. */
export function luminance(hex: string): number {
  const n = hexToNumber(hex);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Mix two #RRGGBB colours. `t` of 0 returns `a`, 1 returns `b`. */
export function mixHex(a: string, b: string, t: number): string {
  const na = hexToNumber(a);
  const nb = hexToNumber(b);
  const k = Math.min(1, Math.max(0, t));
  const r = Math.round(((na >> 16) & 0xff) * (1 - k) + ((nb >> 16) & 0xff) * k);
  const g = Math.round(((na >> 8) & 0xff) * (1 - k) + ((nb >> 8) & 0xff) * k);
  const bl = Math.round((na & 0xff) * (1 - k) + (nb & 0xff) * k);
  return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0')}`;
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
